// Story 6.22 (ADO-1201) — Admin auth-audit-log read client.
//
// Thin typed wrapper over the Bearer-attaching `apiFetch` transport for the
// Story 6.22 `GET /admin/audit-log` endpoint (FR11 forensic/compliance surface).
// Mirrors `users.client.ts` exactly: it is the SINGLE consumer seam until the
// `openapi-typescript`-generated client lands (Story 6.1 deviation D-1), at which
// point these calls move to the generated surface.
//
// The read is tenant-scoped + role-gated server-side; this client only shapes the
// query string and guards the response envelope. Cursor pagination matches the
// server's `(occurred_at desc, id desc)` total order (AC3/AC4): the caller passes
// the previous page's `nextCursor` to fetch the next page.

import { API_PREFIX, apiFetch } from './shoplink-api';

export const AUDIT_LOG_PATH = `${API_PREFIX}/admin/audit-log`;

/** Wire shape of one row from `GET /admin/audit-log` (Story 6.22 contract). */
export interface AuditLogEntry {
  /** Row primary key (uuid) — the table `rowKey` + the cursor tie-break. */
  readonly id: string;
  /** Subject of the event — the user's Auth0 `sub`. */
  readonly sub: string;
  /** Event-kind discriminant (e.g. `LoginSuccess`, `RoleAssigned`). */
  readonly kind: string;
  /** What initiated the event — `auth0`/`system`/`admin:{sub}`/`self`. */
  readonly actor: string;
  /** Event-native timestamp (ISO-8601 UTC). */
  readonly occurredAt: string;
  /** Persist/ingest time (ISO-8601 UTC). */
  readonly recordedAt: string;
  /** Kind-specific variant fields, as persisted (admin forensic view, D-4). */
  readonly payload: Record<string, unknown>;
}

/** The exclusive next-page cursor — the previous page's last (oldest) row. */
export interface AuditLogCursor {
  readonly occurredAt: string;
  readonly id: string;
}

/** A page of the audit log: the rows + the cursor for the next page (null on the last). */
export interface AuditLogPage {
  readonly entries: AuditLogEntry[];
  readonly nextCursor: AuditLogCursor | null;
}

/** Filters + paging for `listAuditLog` (all optional). */
export interface AuditLogQuery {
  /** Subject (`sub`) equality filter. */
  readonly sub?: string;
  /** Event-kind equality filter. */
  readonly kind?: string;
  /** Inclusive lower bound on `occurredAt` (ISO-8601). */
  readonly from?: string;
  /** Inclusive upper bound on `occurredAt` (ISO-8601). */
  readonly to?: string;
  /** Page size (bounded server-side; never widens the seam ceiling). */
  readonly limit?: number;
  /** Exclusive cursor from a previous page's `nextCursor`. */
  readonly cursor?: AuditLogCursor | null;
}

/**
 * Read a page of the tenant's auth-audit log. Throws on a non-2xx so the UI can
 * surface a load-error banner. Guards the response envelope: a 2xx with a
 * non-object body / non-array `entries` (contract drift, a proxy 200-HTML, or an
 * envelope) degrades to the load-error banner rather than white-screening the
 * table (mirrors `listUsers`).
 */
export async function listAuditLog(query: AuditLogQuery = {}): Promise<AuditLogPage> {
  const params = new URLSearchParams();
  if (query.sub !== undefined && query.sub.trim() !== '') params.set('sub', query.sub.trim());
  if (query.kind !== undefined && query.kind.trim() !== '') params.set('kind', query.kind.trim());
  if (query.from !== undefined && query.from.trim() !== '') params.set('from', query.from.trim());
  if (query.to !== undefined && query.to.trim() !== '') params.set('to', query.to.trim());
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  if (query.cursor != null) {
    params.set('beforeOccurredAt', query.cursor.occurredAt);
    params.set('beforeId', query.cursor.id);
  }
  const qs = params.toString();
  const res = await apiFetch(qs === '' ? AUDIT_LOG_PATH : `${AUDIT_LOG_PATH}?${qs}`);
  if (!res.ok) throw new Error(`Failed to load audit log (${res.status})`);
  const data = (await res.json()) as unknown;
  if (!isAuditLogPage(data)) throw new Error('Unexpected audit-log response shape');
  // Normalise a drifted/omitted nextCursor to an explicit null so the caller's
  // `nextCursor === null` last-page checks never see `undefined`.
  return { entries: data.entries, nextCursor: data.nextCursor ?? null };
}

/** Response-envelope guard — `{ entries: [], nextCursor: {...} | null }`. */
function isAuditLogPage(data: unknown): data is AuditLogPage {
  if (typeof data !== 'object' || data === null) return false;
  const envelope = data as { entries?: unknown; nextCursor?: unknown };
  if (!Array.isArray(envelope.entries)) return false;
  // Validate each row too — a drifted entry (e.g. a null/absent `payload`) must
  // degrade to the load-error banner, not crash the table renderer downstream.
  if (!envelope.entries.every(isAuditLogEntry)) return false;
  const cursor = envelope.nextCursor;
  if (cursor === null || cursor === undefined) return true;
  if (typeof cursor !== 'object') return false;
  const c = cursor as { occurredAt?: unknown; id?: unknown };
  return typeof c.occurredAt === 'string' && typeof c.id === 'string';
}

/** Entry-shape guard — every wire field present and correctly typed. The screen
 *  dereferences `payload` via `Object.keys`, so it must be a non-null object. */
function isAuditLogEntry(entry: unknown): entry is AuditLogEntry {
  if (typeof entry !== 'object' || entry === null) return false;
  const e = entry as Record<string, unknown>;
  return (
    typeof e.id === 'string' &&
    typeof e.sub === 'string' &&
    typeof e.kind === 'string' &&
    typeof e.actor === 'string' &&
    typeof e.occurredAt === 'string' &&
    typeof e.recordedAt === 'string' &&
    typeof e.payload === 'object' &&
    e.payload !== null
  );
}
